import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { profileUrl } from "../utils/api";

const fmtDate = (d) => (d ? new Date(String(d).replace(" ", "T")).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—");
const fmtDateTime = (d) => (d ? new Date(String(d).replace(" ", "T")).toLocaleString() : "—");
const METHOD = { email: "Email and password", google: "Google sign-in" };

// Same rules the server enforces (AuthController.passwordProblem).
function passwordProblem(pw) {
  if (!pw || pw.length < 8) return "Password must be at least 8 characters long.";
  if (!/[A-Z]/.test(pw)) return "Password must contain at least one uppercase letter.";
  if (!/[0-9]/.test(pw)) return "Password must contain at least one number.";
  if (!/[^A-Za-z0-9]/.test(pw)) return "Password must contain at least one special character.";
  return "";
}

/**
 * Account → Account details: the private information behind the account (sign-in email,
 * sign-in method, name, phone, age, plan, dates, Terms acceptance), with editing for name
 * and phone and a password change for email/password accounts.
 */
export default function AccountDetailsPanel() {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", phoneNumber: "" });
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get("/api/account/details").then((r) => setD(r.data)).catch(() => setErr("Could not load your account details."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setForm({ firstName: d.firstName || "", lastName: d.lastName || "", phoneNumber: d.phoneNumber || "" });
    setEditing(true); setMsg(""); setErr("");
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (!form.firstName.trim()) { setErr("First name is required."); return; }
    const digits = form.phoneNumber.replace(/\D/g, "");
    if (form.phoneNumber.trim() && digits.length < 10) { setErr("Please enter a valid phone number (at least 10 digits)."); return; }
    setBusy(true);
    try {
      await api.put(`/api/auth/user/${d.userId}`, {
        firstName: form.firstName.trim(), lastName: form.lastName.trim(), phoneNumber: form.phoneNumber.trim(),
      });
      setEditing(false);
      setMsg("Your details have been saved.");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Could not save your details.");
    }
    setBusy(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    if (d.hasPassword && !pw.current) { setErr("Enter your current password."); return; }
    const problem = passwordProblem(pw.next);
    if (problem) { setErr(problem); return; }
    if (pw.next !== pw.confirm) { setErr("The new passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/reset-password", { oldPassword: pw.current || undefined, newPassword: pw.next });
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
      setMsg("Your password has been changed.");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Could not change your password.");
    }
    setBusy(false);
  };

  if (!d) return err ? <p className="acct-error" role="alert">{err}</p> : null;

  return (
    <section className="acct-settings acct-details" id="details" aria-labelledby="acct-details-heading">
      <h2 id="acct-details-heading">Account details</h2>
      <p className="acct-settings-note">Private — only you (and SaatSaheli administrators) can see this.</p>
      {msg && <p className="acct-settings-ok" role="status">{msg}</p>}
      {err && <p className="acct-error" role="alert">{err}</p>}

      {!editing ? (
        <dl className="acct-details-list">
          <dt>Name</dt><dd>{[d.firstName, d.lastName].filter(Boolean).join(" ") || "—"}</dd>
          <dt>Sign-in email</dt><dd>{d.email || "—"}</dd>
          <dt>Sign-in method</dt><dd>{METHOD[(d.signInMethod || "").toLowerCase()] || d.signInMethod || "—"}</dd>
          <dt>Phone</dt><dd>{d.phoneNumber || "—"}</dd>
          <dt>Age</dt><dd>{d.age ?? "—"}</dd>
          <dt>Plan</dt><dd>{d.plan || "Free"} <Link to="/pricing">View plans</Link></dd>
          <dt>Public profile</dt>
          <dd>
            {d.displayName
              ? <Link to={profileUrl(d.userId, d.displayName, d.handle)}>{d.displayName}</Link>
              : "Not set up yet"}{" "}
            · <Link to="/profile">Edit profile</Link>
          </dd>
          <dt>Member since</dt><dd>{fmtDate(d.memberSince)}</dd>
          <dt>Last sign-in</dt><dd>{fmtDateTime(d.lastSignIn)}</dd>
          <dt>Terms accepted</dt><dd>{d.termsAcceptedDate ? `${fmtDate(d.termsAcceptedDate)}${d.termsVersion ? ` (version ${d.termsVersion})` : ""}` : "—"}</dd>
        </dl>
      ) : (
        <form className="acct-details-form" onSubmit={saveDetails}>
          <label>First name *<input type="text" value={form.firstName} maxLength={100} autoComplete="given-name"
            onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
          <label>Last name<input type="text" value={form.lastName} maxLength={100} autoComplete="family-name"
            onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></label>
          <label>Phone<input type="tel" value={form.phoneNumber} maxLength={30} autoComplete="tel"
            onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} /></label>
          <div className="acct-settings-delete-actions">
            <button type="submit" className="acct-profile-link" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => { setEditing(false); setErr(""); }}>Cancel</button>
          </div>
        </form>
      )}

      {!editing && (
        <div className="acct-details-actions">
          <button type="button" className="acct-profile-link" onClick={startEdit}>Edit name or phone</button>
          {!pwOpen && (
            <button type="button" className="acct-profile-link acct-profile-link-secondary"
              onClick={() => { setPwOpen(true); setMsg(""); setErr(""); }}>
              {d.hasPassword ? "Change password" : "Set a password"}
            </button>
          )}
        </div>
      )}

      {pwOpen && (
        <form className="acct-details-form" onSubmit={changePassword}>
          {d.hasPassword && (
            <label>Current password<input type="password" value={pw.current} autoComplete="current-password"
              onChange={(e) => setPw({ ...pw, current: e.target.value })} /></label>
          )}
          <label>New password<input type="password" value={pw.next} autoComplete="new-password"
            onChange={(e) => setPw({ ...pw, next: e.target.value })} /></label>
          <label>Confirm new password<input type="password" value={pw.confirm} autoComplete="new-password"
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })} /></label>
          <p className="acct-settings-note">At least 8 characters, with an uppercase letter, a number and a special character.</p>
          <div className="acct-settings-delete-actions">
            <button type="submit" className="acct-profile-link" disabled={busy}>{busy ? "Saving…" : "Save password"}</button>
            <button type="button" onClick={() => { setPwOpen(false); setPw({ current: "", next: "", confirm: "" }); setErr(""); }}>Cancel</button>
          </div>
        </form>
      )}

      <p className="acct-settings-note">
        Need to change your sign-in email? Write to <a href="mailto:avikaventures.info@gmail.com?subject=Change%20my%20SaatSaheli%20sign-in%20email">avikaventures.info@gmail.com</a> from your current email.
      </p>
    </section>
  );
}
